import HomeNavBar from "@/components/HomeNavBar/HomeNavBar.vue";
import { get_token } from "@/router";
import Swal from "sweetalert2";
import { append_active_organization_id } from "@/utils/organization_profile";
import {
    build_month_options,
    build_credit_stats,
    build_customer_accounts,
    build_sales_stats,
    format_short_date,
    get_customer_initials,
    get_order_products_label,
    is_credit_order,
    is_order_paid,
} from "../CreditManagement/credit_management_helpers";

const ORDER_POLLING_INTERVAL_MS = 10000;

function format_order_price(value) {
    const amount = Number(value || 0);
    return amount
        .toLocaleString("en-US", {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        })
        .replaceAll(",", " ");
}

export default {
    name: "OrderView",
    components: {
        HomeNavBar,
    },
    data() {
        return ({
            base_profile_id: this.$route.query.base_profile_id,
            all_orders: [],
            loading: false,
            order_statuses: [],
            current_status: "all",
            search_query: "",
            order_filter: "all",
            errors: {},
            orders_polling_interval: null,
            is_fetching_orders: false,
            pinned_open_order_key: "",
            pinned_open_order_index: null,
            active_orders_tab: "orders",
            sales_selected_month: "all",
            sales_payment_filter: "all",
            sales_delivery_filter: "all",
            show_mobile_order_filters: false,
            customer_query: "",
            credit_selected_month: "all",
            credit_status_filter: "all",
            show_mobile_credit_filters: false,
            selected_customer_id: null,
            customer_page: 1,
            customer_page_size: 10,
            expanded_customer_account_ids: [],
            settling_order_ids: [],
            updating_order_ids: [],
            updating_order_actions: [],
            is_settling_all_customer_orders: false,
            wallet:{},
            show_withdraw_modal: false,
            withdraw_amount: null,
            withdraw_to: "bank",
            withdraw_account_number: "",
            withdraw_error_message: "",
            creating_shipment_order_ids: [],
        })
    },

    computed: {
        filtered_orders_base() {
            const query = this.search_query.trim().toLowerCase();

            return this.all_orders
                .filter((order) => {
                    return this.order_search_match(order, query)
                        && this.sales_month_match(order)
                        && this.sales_payment_match(order)
                        && this.sales_delivery_match(order);
                })
                .sort((left_order, right_order) => this.sort_orders_with_pending_first(left_order, right_order));
        },
        filtered_orders() {
            const filtered_and_sorted_orders = this.filtered_orders_base.filter((order) => {
                const status = String(order?.order_status_name || "").trim().toLowerCase();
                return this.match_order_status(order, status, this.order_filter);
            });

            return this.keep_open_order_in_place(filtered_and_sorted_orders);
        },
        order_stats() {
            const orders = this.filtered_orders_base;

            return ({
                total: orders.length,
                approved: orders.filter((order) => {
                    const status = String(order?.order_status_name || "").trim().toLowerCase();
                    return ["approved", "accepted"].includes(status);
                }).length,
                rejected: orders.filter((order) => {
                    const status = String(order?.order_status_name || "").trim().toLowerCase();
                    return ["rejected", "cancelled"].includes(status);
                }).length,
                pending: orders.filter((order) => {
                    const status = String(order?.order_status_name || "").trim().toLowerCase();
                    return status === "pending";
                }).length,
                paid: orders.filter((order) => {
                    const status = String(order?.order_status_name || "").trim().toLowerCase();
                    return status === "paid";
                }).length,
                credit: orders.filter((order) => this.is_credit_card_match(order)).length,
            });
        },
        order_filter_buttons() {
            return [
                { label: "All Orders", value: "all", count: this.order_stats.total, icon: "bi bi-grid-1x2" },
                { label: "Pending", value: "pending", count: this.order_stats.pending, icon: "bi bi-hourglass-split" },
                { label: "Approved", value: "approved", count: this.order_stats.approved, icon: "bi bi-bag-check" },
                { label: "Paid", value: "paid", count: this.order_stats.paid, icon: "bi bi-credit-card" },
                { label: "Not Approved", value: "rejected", count: this.order_stats.rejected, icon: "bi bi-x-circle" },
            ];
        },
        order_amount_stats() {
            const totals = {
                total: 0,
                pending: 0,
                approved: 0,
                paid: 0,
                credit: 0,
                rejected: 0,
            };

            this.filtered_orders_base.forEach((order) => {
                const status = String(order?.order_status_name || "").trim().toLowerCase();
                const amount = Number(order?.total_amount || 0);

                totals.total += amount;

                if (status === "pending") {
                    totals.pending += amount;
                    return;
                }

                if (["approved", "accepted"].includes(status)) {
                    totals.approved += amount;
                    return;
                }

                if (status === "paid") {
                    totals.paid += amount;
                }

                if (this.is_credit_card_match(order)) {
                    totals.credit += amount;
                }

                if (["rejected", "cancelled"].includes(status)) {
                    totals.rejected += amount;
                }
            });

            return totals;
        },
        month_options() {
            return build_month_options(this.all_orders);
        },
        sales_orders() {
            return this.all_orders.filter((order) => {
                const status = String(order?.order_status_name || "").trim().toLowerCase();
                return !["rejected", "cancelled"].includes(status)
                    && this.sales_month_match(order)
                    && this.sales_payment_match(order)
                    && this.sales_delivery_match(order);
            });
        },
        sales_stats() {
            return build_sales_stats(this.sales_orders, this.all_orders);
        },
        all_credit_orders() {
            return this.all_orders.filter((order) => this.is_credit_dashboard_order(order));
        },
        filtered_credit_orders() {
            const query = this.customer_query.trim().toLowerCase();

            return this.all_credit_orders.filter((order) => this.matches_credit_order_filters(order, query));
        },
        customer_accounts() {
            return build_customer_accounts(this.filtered_credit_orders);
        },
        credit_stats() {
            return build_credit_stats(this.filtered_credit_orders, this.customer_accounts);
        },
        customer_total_pages() {
            return Math.max(1, Math.ceil(this.customer_accounts.length / this.customer_page_size));
        },
        customer_page_start() {
            if (!this.customer_accounts.length) return 0;
            return ((this.customer_page - 1) * this.customer_page_size) + 1;
        },
        customer_page_end() {
            if (!this.customer_accounts.length) return 0;
            return Math.min(this.customer_page * this.customer_page_size, this.customer_accounts.length);
        },
        paginated_customer_accounts() {
            const start = (this.customer_page - 1) * this.customer_page_size;
            const end = start + this.customer_page_size;
            return this.customer_accounts.slice(start, end);
        },
        selected_customer() {
            if (!this.selected_customer_id) return null;
            return this.customer_accounts.find((customer) => customer.id === this.selected_customer_id) || null;
        },
        selected_customer_orders() {
            return this.selected_customer?.orders || [];
        },
        selected_customer_unpaid_orders() {
            return this.selected_customer_orders.filter((order) => !is_order_paid(order));
        },
        selected_customer_paid_orders() {
            return this.selected_customer_orders.filter((order) => is_order_paid(order));
        },
        selected_customer_unpaid_total() {
            return this.selected_customer_unpaid_orders.reduce((sum, order) => {
                return sum + Number(order?.total_amount || 0);
            }, 0);
        },
        pending_release_orders() {
            const now = new Date();
            const windowMs = 7 * 24 * 60 * 60 * 1000;

            return this.all_orders
                .filter((order) => {
                    const status = String(order?.order_status_name).trim().toLowerCase();
                    if (status !== "paid") return false;

                    const processedAt = new Date(order?.updated_at);
                    if (Number.isNaN(processedAt.getTime())) return false;

                    return processedAt <= now && now - processedAt <= windowMs;
                })
                .sort((leftOrder, rightOrder) => {
                    return new Date(rightOrder?.updated_at).getTime() - new Date(leftOrder?.updated_at).getTime();
                });
        },
    },
    watch: {
        customer_query() {
            this.customer_page = 1;
            this.selected_customer_id = null;
        },
        credit_selected_month() {
            this.customer_page = 1;
            this.selected_customer_id = null;
        },
        credit_status_filter() {
            this.customer_page = 1;
            this.selected_customer_id = null;
        },
        customer_accounts: {
            handler(accounts) {
                if (!accounts.length) {
                    this.selected_customer_id = null;
                    this.customer_page = 1;
                    return;
                }

                if (this.customer_page > this.customer_total_pages) {
                    this.customer_page = this.customer_total_pages;
                }

                if (!this.selected_customer_id) {
                    return;
                }

                const customer_still_exists = accounts.some((customer) => customer.id === this.selected_customer_id);
                if (!customer_still_exists) {
                    this.selected_customer_id = null;
                }
            },
            immediate: true,
        },
        active_orders_tab(next_tab) {
            if (next_tab !== "credit") {
                this.selected_customer_id = null;
            }
            this.show_mobile_order_filters = false;
            this.show_mobile_credit_filters = false;
        },
    },
    methods: {
        open_withdraw_modal() {
            this.show_withdraw_modal = true;
            this.withdraw_error_message = "";
            if (this.withdraw_amount === null || this.withdraw_amount === undefined) {
                this.withdraw_amount = 0;
            }
        },
        close_withdraw_modal() {
            this.show_withdraw_modal = false;
            this.withdraw_error_message = "";
        },
        set_withdraw_amount(value) {
            const amount = Number(value || 0);
            this.withdraw_amount = Number.isFinite(amount) ? amount : 0;
            this.withdraw_error_message = "";
        },
        set_withdraw_amount_max() {
            const available = Number(this.wallet?.available_amount || 0);
            this.withdraw_amount = Number.isFinite(available) ? Math.max(0, Math.floor(available)) : 0;
            this.withdraw_error_message = "";
        },
        review_withdrawal() {
            const available = Number(this.wallet?.available_amount || 0);
            const amount = Number(this.withdraw_amount || 0);

            this.withdraw_error_message = "";

            if (!Number.isFinite(amount) || amount < 0) {
                this.withdraw_error_message = "Minimum withdrawal amount is R0.";
                return;
            }

            if (Number.isFinite(available) && amount > available) {
                this.withdraw_error_message = "Withdrawal amount cannot be more than the available balance.";
                return;
            }

            if (!String(this.withdraw_account_number || "").trim()) {
                this.withdraw_error_message = "Please enter an account number to withdraw to.";
                return;
            }
        },
        get_pending_release_days_remaining(order) {
            const processedAt = new Date(order?.updated_at);
            if (Number.isNaN(processedAt.getTime())) return null;

            const now = new Date();
            const windowMs = 7 * 24 * 60 * 60 * 1000;
            const elapsedMs = now.getTime() - processedAt.getTime();
            const remainingMs = windowMs - elapsedMs;
            const remainingDays = Math.max(0, Math.ceil(remainingMs / (24 * 60 * 60 * 1000)));

            return remainingDays;
        },
        format_pending_release_label(order) {
            const remainingDays = this.get_pending_release_days_remaining(order);
            if (remainingDays === null) return "Release date unknown";

            if (remainingDays === 0) return "Releases today";
            if (remainingDays === 1) return "Releases in 1 day";
            return `Releases in ${remainingDays} days`;
        },
        get_order_item_key(order) {
            return String(
                order?.id ||
                order?.order_id ||
                order?.order_number ||
                order?.created_at ||
                ""
            ).trim();
        },
        merge_order_expansion_state(next_orders = []) {
            const expanded_state = new Map(
                this.all_orders.map((order) => [this.get_order_item_key(order), Boolean(order?.is_expanded)])
            );

            return next_orders.map((order) => {
                const order_key = this.get_order_item_key(order);

                return {
                    ...order,
                    is_expanded: expanded_state.get(order_key) || false,
                };
            });
        },
        get_orders(show_loading = true) {
            if (this.is_fetching_orders) return Promise.resolve(null);
            if (!show_loading && this.has_updating_orders()) return Promise.resolve(null);

            const payload = append_active_organization_id({
                base_profile_id: this.base_profile_id
            });
            this.is_fetching_orders = true;
            if (show_loading) {
                this.loading = true;
            }

            return new Promise((resolve) => {
                $.ajax({
                    type: "POST",
                    url: `${process.env.VUE_APP_BACKEND_API_URL}/company_management/get_orders/`,
                    headers: {
                        Authorization: `Token ${get_token()}`,
                    },
                    data: JSON.stringify(payload),
                    contentType: "application/json",
                    success: (response) => {
                        if (response.status === "success") {
                            this.all_orders = this.merge_order_expansion_state(response.data || []);
                            this.wallet = response.wallet;
                        }
                        resolve(response);
                    },
                    complete: () => {
                        if (show_loading) {
                            this.loading = false;
                        }
                        this.is_fetching_orders = false;
                    },
                    error: (xhr) => {
                        if (xhr.responseJSON && xhr.responseJSON.errors) {
                            this.errors = xhr.responseJSON.errors;
                        }
                        resolve(null);
                    }
                });
            });
        },
        start_orders_polling() {
            this.stop_orders_polling();
            this.orders_polling_interval = setInterval(() => {
                if (this.has_updating_orders()) return;
                this.get_orders(false);
            }, ORDER_POLLING_INTERVAL_MS);
        },
        stop_orders_polling() {
            if (!this.orders_polling_interval) return;
            clearInterval(this.orders_polling_interval);
            this.orders_polling_interval = null;
        },

        get_order_statuses() {
            $.ajax({
                type: "GET",
                url: `${process.env.VUE_APP_BACKEND_API_URL}/company_management/get_order_statuses/`,
                headers: {
                    Authorization: `Token ${get_token()}`,
                },
                success: (response) => {
                    if (response.status === "success") {
                        const seen = new Set();
                        this.order_statuses = response.data.filter((item) => {
                            const status =
                                typeof item === "string"
                                    ? item
                                    : (item.name || item.status || "");
                            if (!status) return false;
                            const normalized = status.toLowerCase().trim();
                            if (seen.has(normalized)) return false;
                            seen.add(normalized);
                            return true;
                        });
                    }
                },
                error: (xhr) => {
                    if (xhr.responseJSON && xhr.responseJSON.errors) {
                        this.errors = xhr.responseJSON.errors;
                    }
                }
            });
        },
        is_credit_card_match(order) {
            const status = String(order?.order_status_name || "").trim().toLowerCase();
            return this.is_credit_order(order) || status === "credit";
        },
        match_order_status(order, orderStatus, filterStatus) {
            if (!filterStatus || filterStatus === "all") return true;
            const status = orderStatus.toLowerCase();
            const filter = filterStatus.toLowerCase();

            if (filter === "credit") {
                return this.is_credit_card_match(order);
            }

            if (filter === "approved") {
                return ["approved", "accepted"].includes(status);
            }

            if (filter === "rejected") {
                return ["rejected", "cancelled"].includes(status);
            }

            return status === filter;
        },
        order_search_match(order, query) {
            if (!query) return true;
            const buyer = order.buyer_name ? order.buyer_name.toLowerCase() : "";
            const number = order.order_number ? order.order_number.toLowerCase() : "";
            return buyer.includes(query) || number.includes(query);
        },
        sales_month_match(order) {
            if (this.sales_selected_month === "all") return true;
            const order_date = new Date(order?.created_at || "");
            if (Number.isNaN(order_date.getTime())) return false;

            const month_key = `${order_date.getFullYear()}-${`${order_date.getMonth() + 1}`.padStart(2, "0")}`;
            return month_key === this.sales_selected_month;
        },
        sales_payment_match(order) {
            if (this.sales_payment_filter === "all") return true;
            if (this.sales_payment_filter === "credit") return is_credit_order(order);
            if (this.sales_payment_filter === "paid") return !is_credit_order(order);
            return true;
        },
        sales_delivery_match(order) {
            if (this.sales_delivery_filter === "all") return true;
            if (this.sales_delivery_filter === "collection") return !order?.pargo_delivery;
            if (this.sales_delivery_filter === "delivery") return !!order?.pargo_delivery;
            return true;
        },
        is_credit_dashboard_order(order) {
            const status = String(order?.order_status_name || order?.order_status || "").trim().toLowerCase();
            return status === "credit" || (status === "paid" && is_credit_order(order));
        },
        get_order_month_key(order) {
            const order_date = new Date(order?.created_at || "");
            if (Number.isNaN(order_date.getTime())) return "";

            return `${order_date.getFullYear()}-${`${order_date.getMonth() + 1}`.padStart(2, "0")}`;
        },
        credit_month_match(order) {
            if (this.credit_selected_month === "all") return true;
            return this.get_order_month_key(order) === this.credit_selected_month;
        },
        credit_status_match(order) {
            if (this.credit_status_filter === "settled") return is_order_paid(order);
            if (this.credit_status_filter === "outstanding") return !is_order_paid(order);
            return true;
        },
        credit_query_match(order, query) {
            if (!query) return true;

            return String(order?.buyer_name || "").toLowerCase().includes(query)
                || String(order?.order_number || "").toLowerCase().includes(query)
                || get_order_products_label(order).toLowerCase().includes(query);
        },
        matches_credit_order_filters(order, query) {
            return this.credit_month_match(order)
                && this.credit_status_match(order)
                && this.credit_query_match(order, query);
        },
        sort_orders_with_pending_first(left_order, right_order) {
            const left_status = String(left_order?.order_status_name || "").trim().toLowerCase();
            const right_status = String(right_order?.order_status_name || "").trim().toLowerCase();
            const left_priority = left_status === "pending" ? 0 : 1;
            const right_priority = right_status === "pending" ? 0 : 1;

            return left_priority - right_priority;
        },
        keep_open_order_in_place(orders = []) {
            if (!this.pinned_open_order_key || this.pinned_open_order_index === null) {
                return orders;
            }

            const pinned_order_index = orders.findIndex((order) =>
                this.get_order_item_key(order) === this.pinned_open_order_key && Boolean(order?.is_expanded)
            );

            if (pinned_order_index === -1) {
                return orders;
            }

            const reordered_orders = [...orders];
            const pinned_orders = reordered_orders.splice(pinned_order_index, 1);
            const pinned_order = pinned_orders.length ? pinned_orders[0] : null;
            const target_index = Math.max(0, Math.min(this.pinned_open_order_index, reordered_orders.length));

            if (!pinned_order) {
                return orders;
            }

            reordered_orders.splice(target_index, 0, pinned_order);
            return reordered_orders;
        },

        set_filter(value) {
            this.order_filter = value;

        },
        scroll_stats(direction) {
            const container = this.$refs?.statsScroller;
            if (!container) return;

            const amount = Math.max(container.clientWidth * 0.8, 220);
            container.scrollBy({
                left: direction === "right" ? amount : -amount,
                behavior: "smooth",
            });
        },
        toggle_expand(order) {
            const order_key = this.get_order_item_key(order);

            if (order.is_expanded) {
                order.is_expanded = false;

                if (this.pinned_open_order_key === order_key) {
                    this.pinned_open_order_key = "";
                    this.pinned_open_order_index = null;
                }
                return;
            }

            const visible_index = this.filtered_orders.findIndex(
                (visible_order) => this.get_order_item_key(visible_order) === order_key
            );

            this.pinned_open_order_key = order_key;
            this.pinned_open_order_index = visible_index === -1 ? 0 : visible_index;
            order.is_expanded = true;
        },

        format_date_time(dateString) {
            if (!dateString) return 'N/A';
            return new Date(dateString).toLocaleString('en-US', {
                year: 'numeric',
                month: 'short',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
            });
        },
        format_attribute_text(value) {
            return String(value || "")
                .trim()
                .split(/[\s_-]+/)
                .filter(Boolean)
                .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
                .join(" ");
        },
        pick_first_attribute_value(...values) {
            return values.find((value) => value !== undefined && value !== null && value !== "") || "";
        },
        get_attribute_label(attribute) {
            return this.pick_first_attribute_value(
                attribute?.attribute_type?.name,
                attribute?.attribute_type,
                attribute?.name
            );
        },
        get_attribute_value(attribute) {
            return this.pick_first_attribute_value(
                attribute?.label,
                attribute?.value?.value,
                attribute?.value?.name,
                attribute?.value,
                attribute?.attribute_value?.value,
                attribute?.attribute_value?.name
            );
        },
        get_item_attributes(item) {
            const rawAttributes =
                item?.attributes ||
                item?.product_attributes ||
                item?.attribute_values ||
                [];

            if (!Array.isArray(rawAttributes) || !rawAttributes.length) return [];

            return rawAttributes
                .map((attribute) => {
                    const rawLabel = this.get_attribute_label(attribute);
                    const rawValue = this.get_attribute_value(attribute);

                    const label = this.format_attribute_text(rawLabel);
                    const value = this.format_attribute_text(rawValue);

                    if (!label || !value) return null;

                    return { label, value };
                })
                .filter(Boolean);
        },
        format_price: format_order_price,
        format_short_date,
        get_order_products_label,
        get_customer_initials,
        is_order_paid,
        is_credit_order,
        select_customer(customer) {
            this.selected_customer_id = customer?.id || null;
        },
        clear_selected_customer() {
            this.selected_customer_id = null;
        },
        previous_customer_page() {
            if (this.customer_page <= 1) return;
            this.customer_page -= 1;
        },
        next_customer_page() {
            if (this.customer_page >= this.customer_total_pages) return;
            this.customer_page += 1;
        },
        customer_has_unpaid_orders(customer) {
            return Number(customer?.outstanding_amount || 0) > 0;
        },
        is_customer_account_expanded(customer) {
            return this.expanded_customer_account_ids.includes(customer?.id);
        },
        toggle_customer_account(customer) {
            if (!customer?.id) return;

            if (this.is_customer_account_expanded(customer)) {
                this.expanded_customer_account_ids = this.expanded_customer_account_ids.filter((id) => id !== customer.id);
                return;
            }

            this.expanded_customer_account_ids = [...this.expanded_customer_account_ids, customer.id];
        },
        is_settling_order(order_id) {
            return this.settling_order_ids.includes(order_id);
        },
        is_creating_shipment(order_id) {
            return this.creating_shipment_order_ids.includes(order_id);
        },
        is_updating_order(order_id) {
            return this.updating_order_ids.includes(order_id);
        },
        is_updating_order_action(order_id, action) {
            return this.updating_order_actions.some((item) =>
                item.order_id === order_id && item.action === action
            );
        },
        has_updating_orders() {
            return this.updating_order_ids.length > 0;
        },
        async settle_order(order) {
            const order_id = order?.id;
            if (!order_id || this.is_settling_order(order_id)) return;

            this.settling_order_ids = [...this.settling_order_ids, order_id];
            await this.$nextTick();

            try {
                await this.update_order(order_id, "paid");
            } finally {
                this.settling_order_ids = this.settling_order_ids.filter((id) => id !== order_id);
            }
        },
        async settle_all_customer_orders() {
            if (!this.selected_customer_unpaid_orders.length || this.is_settling_all_customer_orders) return;

            this.is_settling_all_customer_orders = true;
            await this.$nextTick();

            try {
                for (const order of this.selected_customer_unpaid_orders) {
                    await this.settle_order(order);
                }
            } finally {
                this.is_settling_all_customer_orders = false;
            }
        },
        async reject_order(order) {
            const result = await Swal.fire({
                title: "Reject order",
                text: "You can add a reason for rejecting this order.",
                input: "textarea",
                inputPlaceholder: "Reason for rejection (optional)",
                inputAttributes: {
                    "aria-label": "Reason for rejection",
                },
                showCancelButton: true,
                confirmButtonText: "Reject",
                confirmButtonColor: "#dc3545",
                cancelButtonText: "Cancel",
            });

            if (!result.isConfirmed) return;

            return this.update_order(order.id, "rejected", result.value);
        },
        request_order_status_update(order_id, new_status, reason = "") {
            return this.update_order(order_id, new_status, reason);
        },
        async update_order(order_id, new_status, reason = "") {
            if (!order_id || this.is_updating_order(order_id)) return;

            const rejection_reason = String(reason || "").trim();
            const payload = {
                base_profile_id: this.base_profile_id,
                order_id: order_id,
                status: new_status,
            };

            if (rejection_reason) {
                payload.reason = rejection_reason;
            }

            this.updating_order_ids = [...this.updating_order_ids, order_id];
            this.updating_order_actions = [
                ...this.updating_order_actions.filter((item) => item.order_id !== order_id),
                {
                    order_id,
                    action: new_status,
                },
            ];
            await this.$nextTick();

            return new Promise((resolve, reject) => {
                $.ajax({
                    type: "POST",
                    url: `${process.env.VUE_APP_BACKEND_API_URL}/company_management/update_order_status/`,
                    headers: {
                        Authorization: `Token ${get_token()}`,
                    },
                    data: JSON.stringify(payload),
                    contentType: "application/json",
                    success: (response) => {
                        if (response.status === "success") {
                            const order = this.all_orders.find((order) => order.id === order_id);
                            if (order) {
                                order.order_status_name =
                                    response?.data?.order_status_name ||
                                    response?.data?.order?.order_status_name ||
                                    response?.order?.order_status_name ||
                                    new_status;
                            }
                            resolve(response);
                            return;
                        }

                        Swal.fire({
                            icon: "error",
                            title: "Status not updated",
                            text: response?.message || "The backend did not confirm the status change.",
                        });
                        reject(response);
                    },
                    error: (xhr) => {
                        if (xhr.responseJSON && xhr.responseJSON.errors) {
                            this.errors = xhr.responseJSON.errors;
                        }
                        Swal.fire({
                            icon: "error",
                            title: "Status not updated",
                            text: xhr.responseJSON?.message || xhr.responseJSON?.error || "Failed to update order status.",
                        });
                        reject(xhr);
                    },
                    complete: () => {
                        if (!this.is_updating_order(order_id)) return;
                        this.updating_order_ids = this.updating_order_ids.filter((id) => id !== order_id);
                        this.updating_order_actions = this.updating_order_actions.filter((item) => item.order_id !== order_id);
                    }
                });
            });
        }
    },
    mounted() {
        this.get_orders();
        this.get_order_statuses();
        this.start_orders_polling();
    },
    beforeUnmount() {
        this.stop_orders_polling();
    },
}
